import { Model } from 'survey-core';
import { ISurveyTemplateDto } from '@vivli/shared/infrastructure/interface';

/**
 * DEV-641: picks the survey template a request should be measured against - the version stamped on
 * the request if we still have that template, otherwise the current one. The Research Proposal tab
 * and the Submit gate both resolve their template through here so they can never disagree about
 * which questions apply.
 */
export const selectSurveyTemplate = (surveyTemplates?: ISurveyTemplateDto[], surveyVersion?: string): ISurveyTemplateDto => {
    const stampedTemplate = surveyVersion ? surveyTemplates?.find((item) => item.surveyVersion === surveyVersion) : null;

    return stampedTemplate ?? surveyTemplates?.find((item) => item.isCurrent);
};

/**
 * DEV-641: the AI/ML survey owns its own validation rules, so this does not reimplement them - it
 * builds the same SurveyJS model the Research Proposal tab builds and asks it the same question.
 * Running the model detached from the DOM is what lets the Submit gate know whether the survey is
 * answered without the user ever having opened that tab.
 *
 * Answers being non-blank is NOT enough on its own: merely mounting the survey writes a payload of
 * hidden defaults and calculated values, so an untouched questionnaire still persists something
 * like {"explainRiskScore":false,"debug":false,"RiskScore":0}. Only the model can tell us that the
 * required questions inside it went unanswered.
 */
export const surveyHasUnansweredQuestions = (surveyTemplate?: string, surveyAnswers?: string): boolean => {
    //nothing to measure against - never block Submit on a survey we cannot evaluate
    if (!surveyTemplate) {
        return false;
    }

    const survey = new Model(surveyTemplate);
    //mirrors the mounted survey so questions hidden by a visibleIf are not held against the user
    survey.showInvisibleElements = false;

    if (surveyAnswers?.trim()) {
        try {
            survey.data = JSON.parse(surveyAnswers);
        } catch {
            //unreadable answers are no answers at all
            return true;
        }
    }

    //fireCallback false - this model is never rendered, so there is nothing to show errors on
    return !survey.validate(false, false);
};
